import type { SideNavV2Item } from "@/components/interactive/SideNavV2";
import {
  TEAM_NAVBAR_ADMIN_SECTION_ITEM_IDS,
  type TeamNavbarLink,
  type TeamNavbarPageId,
} from "@/components/navigation/TeamNavbar.constants";
import { useTeamNavbarCounts } from "@/components/navigation/useNavbarCounts";
import { useTeamNavbarLinks } from "@/components/navigation/useTeamNavbarLinks";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useCallback, useMemo } from "react";

/**
 * The global settings destinations as sidebar rows, shared by the classic team navbar and the
 * category nav's global settings sidebar. Admin links come apart from the rest because both
 * navigations group them under their own Admin heading.
 */
export const useTeamSideNavItems = (component: string) => {
  const { environmentId, projectId, team, teamService } = useRouteAndTeamInfo();
  const teamNavbarLinks = useTeamNavbarLinks();
  const teamNavbarCounts = useTeamNavbarCounts();
  const teamMemberCount = teamService.loaded ? teamService.members.length : undefined;
  const trackNavbarClick = useAmplitudeOnClick({
    event: AmplitudeEvent.TEAM_NAVBAR_CLICK,
    component,
    href: "",
    text: "",
    description: "",
  });
  const toSideNavItem = useCallback(
    (link: TeamNavbarLink<TeamNavbarPageId>): SideNavV2Item<string> => {
      const href = link.href();
      const label = link.text;
      const fallbackCount =
        link.id === "projects"
          ? team?.projects.length
          : link.id === "settings"
            ? teamMemberCount
            : undefined;

      return {
        id: link.id,
        label,
        href,
        icon: link.icon,
        admin: link.admin,
        // Prefer the RPC count; fall back to the already-loaded team
        // data for badges not supplied by the inventory endpoint.
        count: teamNavbarCounts[link.id] ?? fallbackCount,
        onSelect: () => {
          trackNavbarClick({
            href,
            text: label,
            mainPageId: link.id,
            projectId,
            environmentId,
          });
        },
      };
    },
    [
      environmentId,
      projectId,
      teamMemberCount,
      team?.projects.length,
      teamNavbarCounts,
      trackNavbarClick,
    ],
  );

  return useMemo(() => {
    const adminItemIds = new Set<string>(TEAM_NAVBAR_ADMIN_SECTION_ITEM_IDS);

    return {
      inlineItems: teamNavbarLinks.filter((link) => !adminItemIds.has(link.id)).map(toSideNavItem),
      // useTeamNavbarLinks already drops admin links for non-employees, so this is empty for them.
      adminItems: teamNavbarLinks.filter((link) => adminItemIds.has(link.id)).map(toSideNavItem),
    };
  }, [teamNavbarLinks, toSideNavItem]);
};
